import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Grid,
  IconButton,
} from '@material-ui/core';
import { Delete as DeleteIcon, SettingsBackupRestore } from '@material-ui/icons';
import { getAlleDiscount } from 'pages/quotes/helpers';
import round from 'helpers/mathRound';
import QuoteItemRowTotal from 'pages/quotes/QuoteItems/QuoteListings/QuoteItem/QuoteItemRowTotal';
import QuoteItemSubscriptionDiscount from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemSubscriptionDiscount';
import QuoteItemSelect from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemSelect';
import QuoteItemSavings from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemSavings';
import QuoteItemUnitPrice from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemUnitPrice';
import QuoteItemQuantity from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemQuantity';
import QuoteItemSubscription from '../../../QuoteItems/QuoteListings/QuoteItem/QuoteItemSubscription';
import { handleSelectDataSource } from '../../../QuoteItems/QuoteListings/utils/handleSelectDataSource';
import { useStyles } from '../../../styles';


const attributesList = ['savings', 'subscription_active', 'subscription_label', 'additional_subscription_price', 'quotepro_discounts'];

const EditQuoteListingsItemRow = ({
  item,
  index,
  selectedQuote,
  formik
}) => {
  const classes = useStyles();

  const {
    subscriptionOptionsBySku,
    quoteProductsByGroup,
  } = useSelector((state: IReduxState) => state.quote);

  const {
    values,
    setFieldValue,
    setValues,
    setErrors,
    setTouched,
    initialValues
  } = formik;

  const { selectedItemGroup, sku } = item;

  const product = useMemo(() => {
    return quoteProductsByGroup?.[selectedItemGroup]?.find((el) => el.sku == sku);
  }, [selectedItemGroup, sku]);

  const selectedDataSource = useMemo(() => {
    return handleSelectDataSource(
      sku,
      true,
      selectedQuote,
      subscriptionOptionsBySku,
      product?.custom_attributes || [],
      attributesList
    );
  }, [sku, selectedQuote, subscriptionOptionsBySku, product?.custom_attributes]);

  const handleDeleteRow = (index) => {
    const { items } = values;
    const updatedItems = items.filter((_, i) => i !== index);

    if (updatedItems.length === 0) {
      setValues(initialValues);
      setTouched({}, false);
      setErrors({});
    } else {
      setFieldValue('items', updatedItems);
    }
  };

  const { subscriptionOptions } = selectedDataSource;

  const handleResetItem = (index) => {
    const { items } = values;
    const foundedItem = items[index];

    const selectedGroup = selectedItemGroup || values.selectedGroup;
    const selectedItem = quoteProductsByGroup?.[selectedGroup]?.find((product) => product.sku === foundedItem?.sku) || {};

    const { sku, custom_attributes = [], price = 0 } = selectedItem;

    const itemGroup = selectedItemGroup || (custom_attributes.find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value?.toString().split(',')[0]);
    const isAlleSKU = sku.includes('bdsku');
    const itemSavings = isAlleSKU ? round(getAlleDiscount(selectedItem)) || 0 : round(custom_attributes.find((attr) => attr.attribute_code === 'savings')?.value) || 0;

    const itemAdditionalSubscriptionPrice = custom_attributes.find((attr) => attr.attribute_code === 'additional_subscription_price')?.value || 0;
    const isSubscriptionActive = custom_attributes.find((attr) => attr.attribute_code === 'subscription_active')?.value === '1' || false;
    const [selectedOptionData] = subscriptionOptions;

    const { installment_price: initialInstallmentPrice = 0, adjustment_price: initialAdjustmentPrice = 0 } = Array.isArray(subscriptionOptionsBySku) ?
      (subscriptionOptions.find((option) => option.id === Number(selectedOptionData?.id)) || { installment_price: 0, adjustment_price: 0 }) :
      { installment_price: 0, adjustment_price: 0 };

    const newPrice = isSubscriptionActive && (initialInstallmentPrice || initialAdjustmentPrice) ?
      round(initialInstallmentPrice + initialAdjustmentPrice) + itemSavings :
      price + itemSavings;

    const product = {
      selectedItemGroup: itemGroup,
      sku,
      qty: 1,
      fixedPrice: newPrice,
      subscription_id: selectedOptionData?.id || '',
      discountType: '',
      subscriptionFrequency: '',
      additional_subscription_price: itemAdditionalSubscriptionPrice,
      savings: itemSavings,
      discount: '',
    };

    setFieldValue(`items[${index}]`, product);
  };

  return (
    <Grid container item xs={12} spacing={1} key={index} className={`${classes.quoteItem} mb-2`}>
      <QuoteItemSelect
        formik={formik}
        item={item}
        index={index}
      />

      <QuoteItemQuantity
        formik={formik}
        item={item}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemUnitPrice
        formik={formik}
        item={item}
        index={index}
        product={product}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemSavings
        formik={formik}
        item={item}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemSubscription
        formik={formik}
        item={item}
        index={index}
        selectedDataSource={selectedDataSource}
        product={product}
      />

      <QuoteItemSubscriptionDiscount
        formik={formik}
        item={item}
        index={index}
        selectedDataSource={selectedDataSource}
      />

      <QuoteItemRowTotal
        item={item}
        selectedDataSource={selectedDataSource}
      />

      <Grid item xs={12} lg="auto">
        <IconButton
          aria-label="restore"
          style={{padding: '8px 0'}}
          disabled={!item?.sku || item?.sku?.includes('bdsku')}
          onClick={() => handleResetItem(index)}
        >
          <SettingsBackupRestore />
        </IconButton>
        <IconButton
          aria-label="delete"
          style={{padding: '8px 0'}}
          onClick={() => handleDeleteRow(index)}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default EditQuoteListingsItemRow;
